import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnDestroy,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import {AbstractWizardStepComponent, Wizard} from "../../../../../../../../common/interfaces/wizard.interface";
import {Base} from "../../../../../../../../common/interfaces/base.interfaces";
import {Warehouse} from "../../../../../../../../common/interfaces/warehouse.interface";
import {Api} from "../../../../../../../../common/services/api.service";
import {Modal, ModalService} from "../../../../../../../services/modal.service";
import {ToastService} from "../../../../../../../../common/services/toast.service";
import {SpinnerService} from "../../../../../../../../common/services/spinner.service";
import {ConfirmComponent} from "src/modules/common/components/confirm/confirm.component";
import {Api3Service} from "../../../../../../../../common/services/api3.service";
import {Table} from "../../../../../../../../common/interfaces/table.interface";
import {WarehouseLocationFormComponent} from "../../../../locations";
import {AvailableLocationsModalComponent} from "../../common/available-locations/list.component";
import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";


@Component({
    selector: "section-warehouse-order-wizard-kit-inbound-location",
    templateUrl: "location.component.html",
    styleUrls: [
        "location.component.scss"
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})

export class WarehouseProceduresWizardKitInboundLocationComponent
    extends AbstractWizardStepComponent implements OnDestroy {

    public outboundComplete: boolean = false;
    public inboundComplete: boolean = false;

    public state: Base.IState;

    public shipment: Warehouse.IShipment;

    public orderRef: string;

    protected destroy$: EventEmitter<boolean> = new EventEmitter(false);

    public listTable: Table.ISettings;

    @ViewChild(ConfirmComponent, {static: false})
    public confirmRef: ConfirmComponent;

    public packages: any[] = [];

    public formGroup: FormGroup = new FormGroup({
        packages: new FormArray([])
    });

    public get packagesArray(): FormArray {
        return this.formGroup.get("packages") as FormArray;
    }

    public constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        private modalService: ModalService,
        private toastService: ToastService,
        private spinnerService: SpinnerService,
        private api3Service: Api3Service,
    ) {
        super(changeDetectorRef);
    }


    private async getAllocations(): Promise<void> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.api3Service.get(
            `${this.state.section}/scan/outbound/${this.shipment.id}/items`, {
                data_structure: "count",
            });
        this.spinnerService.hide();

        if (data) {
            this.outboundComplete = data.count <= 0;

            if (!this.outboundComplete) {
                this.toastService.show("Outbound is not complete", "warning");

                this.result.emit({
                    action: "result",
                    value: false
                });
            }

            this.changeDetectorRef.markForCheck();
        }
    }

    private async getOrder(): Promise<void> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.api3Service.get(
            `${this.state.section}/orders/${this.shipment.order_id}`, {
                relations: [
                    "InboundOrderItem:id,order_id",
                    "InboundOrderItem.WarehouseTransactions:id,order_item_id",
                ]
            });
        this.spinnerService.hide();

        if (data) {
            this.inboundComplete = data.inbound_order_item.warehouse_transactions.length > 0;

            if (this.inboundComplete) {
                this.toastService.show("Inbound already processed", "warning");

                this.result.emit({
                    action: "result",
                    value: true
                });
            }
            this.changeDetectorRef.markForCheck();
        }
    }

    private async getPackages(): Promise<any> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.api3Service.get(
            `${this.state.section}/shipments/${this.shipment.id}/packages`,
            {
                counts: [
                    "WarehouseTransactions"
                ],
                relations: [
                    "WarehousePackageType",
                    "WarehouseTransactions.PartMaster:id,group",
                    "Boxes",
                ],
                sort: {
                    data: this.shipment.partner_id != 14 ? "name" : "id"
                }
            });
        if (data) {
            this.packages = data;

            this.packagesArray.clear();

            for (const p of this.packages) {
                this.packagesArray.push(new FormGroup({
                    package_id: new FormControl(p.id, [Validators.required]),
                    location_id: new FormControl(null, [Validators.required]),
                    location_name: new FormControl(null),
                }));
            }

            this.changeDetectorRef.markForCheck();
        }
        this.spinnerService.hide();
    }

    public async locationList(packageIndex: number): Promise<any> {
        const response: Modal.IResponse = await this.modalService.open(AvailableLocationsModalComponent, {
            orderRef: this.orderRef,
            modalWidth: 1200,
            state: this.state,
            partner_id: this.shipment.partner_id
        });

        if (response) {
            if (response.name === "set") {
                this.packagesArray.at(packageIndex).patchValue({location_id: response.value.id});
                this.packagesArray.at(packageIndex).patchValue({location_name: response.value.location});

                this.changeDetectorRef.markForCheck();

            } else if (response.name === "add") {
                this.addLocation(packageIndex);
            }
        }
    }


    public async addLocation(packageIndex: number): Promise<any> {
        const response: Modal.IResponse = await this.modalService.open(WarehouseLocationFormComponent, {
            location_name: "",
            state: this.state
        });
        if (response && response.value) {
            this.packagesArray.at(packageIndex).patchValue({location_id: response.value.id});
            this.packagesArray.at(packageIndex).patchValue({location_name: response.value.location});
            this.changeDetectorRef.markForCheck();
        }
    }

    public async submit(): Promise<void> {
        if (!await this.confirmRef.confirm(`This action cannot be reverted`, `Save changes?`)) {
            return;
        }

        this.spinnerService.show();

        const {code, message}: Api.IResponse = await this.api3Service.post(
            `${this.state.section}/scan/outbound/${this.shipment.id}/store-kit`, this.formGroup.value);
        this.spinnerService.hide();


        if (code === 200) {
            this.toastService.show(message, "success");

            this.inboundComplete = true;

            this.changeDetectorRef.markForCheck();

            this.result.emit({
                action: "result",
                value: true
            });
        }

    }

    /**
     * Initialize details step
     * @returns {Promise<any>}
     * @param data
     */
    public async init(data: Wizard.IData): Promise<any> {
        this.state = data.state;
        this.shipment = data.shipment;
        this.orderRef = data.orderRef;

        this.getAllocations();
        this.getOrder();
        this.getPackages();
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
